// React Query hooks for the Payment Intermediary CRUD endpoints exposed by
// payment-core under /api/v1/operations/paymentIntermediaries.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import type {
  PaymentIntermediary,
  PaymentIntermediaryCreateRequest,
  PaymentIntermediaryStatusFilter,
  PaymentIntermediaryStatusUpdateRequest,
  PaymentIntermediaryUpdateRequest,
} from '@/types/payment-intermediary';

const BASE = `${appConfig.operationsApi}/paymentIntermediaries`;

export const PAYMENT_INTERMEDIARIES_KEYS = {
  all: ['payment-intermediaries'] as const,
  list: (status: PaymentIntermediaryStatusFilter) =>
    ['payment-intermediaries', 'list', status] as const,
  byId: (id: number) => ['payment-intermediaries', 'detail', id] as const,
};

function unwrap<T>(data: M3ApiResponse<T>): T {
  if (data.code !== 0) throw new Error(data.message || 'Request failed');
  return data.result;
}

export function usePaymentIntermediariesQuery(
  status: PaymentIntermediaryStatusFilter = 'ALL',
) {
  return useQuery({
    queryKey: PAYMENT_INTERMEDIARIES_KEYS.list(status),
    queryFn: async () => {
      const url = status === 'ALL' ? BASE : `${BASE}/${status}`;
      const { data } = await apiClient.get<M3ApiResponse<PaymentIntermediary[]>>(url);
      return unwrap(data) ?? [];
    },
  });
}

export function usePaymentIntermediaryQuery(id: number | null | undefined) {
  return useQuery({
    queryKey:
      id != null
        ? PAYMENT_INTERMEDIARIES_KEYS.byId(id)
        : (['payment-intermediaries', 'detail', null] as const),
    enabled: id != null,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<PaymentIntermediary>>(
        `${BASE}/id/${id}`,
      );
      return unwrap(data);
    },
  });
}

export function useCreatePaymentIntermediaryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PaymentIntermediaryCreateRequest) => {
      const { data } = await apiClient.post<M3ApiResponse<PaymentIntermediary>>(BASE, body);
      return unwrap(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PAYMENT_INTERMEDIARIES_KEYS.all });
    },
  });
}

export function useUpdatePaymentIntermediaryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; body: PaymentIntermediaryUpdateRequest }) => {
      const { data } = await apiClient.put<M3ApiResponse<PaymentIntermediary>>(
        `${BASE}/id/${input.id}`,
        input.body,
      );
      return unwrap(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PAYMENT_INTERMEDIARIES_KEYS.all });
    },
  });
}

export function useUpdatePaymentIntermediaryStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: number;
      body: PaymentIntermediaryStatusUpdateRequest;
    }) => {
      const { data } = await apiClient.patch<M3ApiResponse<PaymentIntermediary>>(
        `${BASE}/id/${input.id}/status`,
        input.body,
      );
      return unwrap(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PAYMENT_INTERMEDIARIES_KEYS.all });
    },
  });
}
